import { useState, useCallback, useRef } from 'react';
import api from '../services/api';

/**
 * OCR Processing Hook for Vehicle Document Analysis
 * 
 * Features:
 * - Single & batch document processing
 * - Auto-fill form data with OCR results
 * - Track which fields came from OCR (for highlighting)
 * - Error handling & retry logic
 * - Progress tracking for batch operations
 * 
 * Usage:
 * const { processDocument, isOCRField, applyOCRToForm, ... } = useOCR();
 */
const useOCR = () => {
    // ✅ CORE STATE
    const [processing, setProcessing] = useState(false);
    const [ocrResults, setOcrResults] = useState({
        ruhsat: null,
        alis: null,
        satis: null
    });
    const [ocrFields, setOcrFields] = useState(new Map()); // Track OCR-filled fields with document type
    const [errors, setErrors] = useState({});
    const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

    // ✅ REF for cleanup
    const processingRef = useRef(false);

    /**
     * OCR Data Mapping - Backend Response → Frontend Form Fields
     */
    const mapOCRDataToFormFields = useCallback((ocrData, documentType) => {
        const mappedData = {};
        const fieldsMapped = new Set();

        console.log(`📋 Mapping OCR data for ${documentType}:`, ocrData);

        switch (documentType) {
            case 'ruhsat':
                console.log('⚠️ DEBUG: Ruhsat OCR disabled - no mapping performed');
                // Ruhsat OCR devre dışı - performans optimizasyonu
                break;
            case 'alis_sozlesmesi':
                // Basic vehicle info from purchase agreement
                if (ocrData.plateNumber) {
                    mappedData.plateNumber = ocrData.plateNumber;
                    fieldsMapped.add('plateNumber');
                }
                if (ocrData.chassisNumber) {
                    mappedData.chassisNumber = ocrData.chassisNumber;
                    fieldsMapped.add('chassisNumber');
                }

                // Purchase financial info
                if (ocrData.purchaseDate) {
                    mappedData.purchaseDate = ocrData.purchaseDate;
                    fieldsMapped.add('purchaseDate');
                }
               /* if (ocrData.purchasePrice) {
                    mappedData.purchasePrice = ocrData.purchasePrice;
                    fieldsMapped.add('purchasePrice');
                } */

                // Seller ID only (no name/address needed)
                if (ocrData.seller?.id) {
                    if (!mappedData.purchasedFrom) mappedData.purchasedFrom = {};
                    mappedData.purchasedFrom.idNumber = ocrData.seller.id;
                    fieldsMapped.add('purchasedFrom.idNumber');
                }
                break;

            case 'satis_sozlesmesi':
                // Sale financial info
                if (ocrData.saleDate) {
                    mappedData.saleDate = ocrData.saleDate;
                    fieldsMapped.add('saleDate');
                }
               /* if (ocrData.salePrice) {
                    mappedData.salePrice = ocrData.salePrice;
                    fieldsMapped.add('salePrice');
                } */

                // Customer ID only (no name/address needed)
                if (ocrData.owner?.id) {
                    if (!mappedData.soldTo) mappedData.soldTo = {};
                    mappedData.soldTo.idNumber = ocrData.owner.id;
                    fieldsMapped.add('soldTo.idNumber');
                }
                break;

            case 'alis_faturasi':
                // Parse invoice number from purchase invoice
                if (ocrData.invoiceNumber) {
                    mappedData.invoiceNumber = ocrData.invoiceNumber;
                    fieldsMapped.add('invoiceNumber');
                }
                break;

            default:
                console.warn(`⚠️ Unknown document type for mapping: ${documentType}`);
        }

        console.log(`✅ Mapped ${fieldsMapped.size} fields for ${documentType}:`,
            Array.from(fieldsMapped));
        console.log(`📄 Mapped data:`, mappedData);

        return { mappedData, fieldsMapped };
    }, []);

    /**
     * Process single document with OCR
     */
    const processDocument = useCallback(async (file, documentType = 'auto') => {
        if (!file) {
            console.error('❌ processDocument: No file provided');
            return { success: false, error: 'Dosya bulunamadı' };
        }

        console.log(`🔍 OCR Processing started: ${file.name} (${documentType})`);

        setProcessing(true);
        processingRef.current = true;

        // Clear previous errors
        setErrors(prev => ({ ...prev, [documentType]: null }));

        try {
            // Prepare FormData
            const formData = new FormData();
            formData.append('document', file);
            formData.append('documentType', documentType);

            console.log(`📤 Sending OCR request for ${file.name}`);

            // API call
            const response = await api.post('/ocr/process', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (!processingRef.current) {
                console.log('⚠️ OCR processing was cancelled');
                return { success: false, error: 'İşlem iptal edildi' };
            }

            console.log(`📥 OCR API Response:`, response.data);

            if (response.data.success) {
                const { documentType: detectedType, extractedData, confidence } = response.data.data;

                console.log(`✅ OCR Success - Type: ${detectedType}, Confidence: ${confidence}%`);
                console.log(`📋 Extracted data:`, extractedData);

                // Map OCR data to form fields
                const { mappedData, fieldsMapped } = mapOCRDataToFormFields(extractedData, detectedType);

                // Update results
                setOcrResults(prev => ({
                    ...prev,
                    [detectedType]: {
                        raw: extractedData,
                        mapped: mappedData,
                        confidence,
                        fileName: file.name,
                        processedAt: new Date().toISOString()
                    }
                }));

                // Update OCR fields tracking
                // Update OCR fields tracking with document type
                setOcrFields(prev => {
                    const newMap = new Map(prev);
                    fieldsMapped.forEach(field => {
                        newMap.set(field, detectedType); // 'alis_sozlesmesi' vs 'satis_sozlesmesi'
                    });
                    return newMap;
                });
                console.log(`🎯 OCR field tracking updated. Total OCR fields: ${fieldsMapped.size}`);

                return {
                    success: true,
                    documentType: detectedType,
                    extractedData,
                    mappedData,
                    confidence,
                    fieldsExtracted: fieldsMapped.size
                };

            } else {
                const errorMsg = response.data.error || 'OCR işlemi başarısız';
                console.error(`❌ OCR Failed:`, errorMsg);

                setErrors(prev => ({ ...prev, [documentType]: errorMsg }));

                return { success: false, error: errorMsg };
            }

        } catch (error) {
            console.error('❌ OCR API Error:', error);

            let errorMessage = 'OCR işlemi sırasında hata oluştu';

            if (error.response?.status === 413) {
                errorMessage = 'Dosya çok büyük (Max: 10MB)';
            } else if (error.response?.status === 415) {
                errorMessage = 'Desteklenmeyen dosya formatı';
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.code === 'NETWORK_ERROR') {
                errorMessage = 'Ağ bağlantısı hatası';
            }

            setErrors(prev => ({ ...prev, [documentType]: errorMessage }));

            return { success: false, error: errorMessage };

        } finally {
            setProcessing(false);
            processingRef.current = false;
        }
    }, [mapOCRDataToFormFields]);

    /**
     * Process multiple documents in batch
     */
    const processBatchDocuments = useCallback(async (documents) => {
        if (!documents || documents.length === 0) {
            console.error('❌ processBatchDocuments: No documents provided');
            return { success: false, error: 'Belge bulunamadı' };
        }

        console.log(`🔍 Batch OCR Processing started: ${documents.length} documents`);

        setProcessing(true);
        processingRef.current = true;
        setBatchProgress({ current: 0, total: documents.length });

        const results = [];
        const allFieldsMapped = new Set();

        try {
            // Process each document sequentially
            for (let i = 0; i < documents.length; i++) {
                if (!processingRef.current) {
                    console.log('⚠️ Batch OCR processing was cancelled');
                    break;
                }

                const doc = documents[i];
                setBatchProgress({ current: i + 1, total: documents.length });

                console.log(`📄 Processing document ${i + 1}/${documents.length}: ${doc.file?.name || doc.name}`);

                const result = await processDocument(doc.file || doc, doc.category || 'auto');
                results.push({
                    ...result,
                    originalDocument: doc
                });

                // Collect all mapped fields
                if (result.success && result.mappedData) {
                    const { fieldsMapped } = mapOCRDataToFormFields(result.extractedData, result.documentType);
                    fieldsMapped.forEach(field => allFieldsMapped.add(field));
                }

                // Small delay between requests to avoid overwhelming the server
                if (i < documents.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            const successCount = results.filter(r => r.success).length;
            const failureCount = results.length - successCount;

            console.log(`✅ Batch OCR completed: ${successCount}/${results.length} successful`);
            console.log(`🎯 Total fields extracted: ${allFieldsMapped.size}`);

            return {
                success: successCount > 0,
                results,
                successCount,
                failureCount,
                totalFieldsExtracted: allFieldsMapped.size
            };

        } catch (error) {
            console.error('❌ Batch OCR Error:', error);
            return { success: false, error: error.message };

        } finally {
            setProcessing(false);
            processingRef.current = false;
            setBatchProgress({ current: 0, total: 0 });
        }
    }, [processDocument, mapOCRDataToFormFields]);

    /**
     * Get extracted data for external application
     */
    const getExtractedData = useCallback(() => {
        const extractedData = {};
        const allFieldsMapped = new Set();

        // Collect data from all processed documents
        Object.entries(ocrResults).forEach(([docType, result]) => {
            if (result && result.mapped) {
                extractedData[docType] = result.mapped;

                // Track which fields were mapped
                Object.keys(result.mapped).forEach(field => {
                    if (field === 'technicalDetails') {
                        // Handle technical details nested fields
                        Object.keys(result.mapped[field]).forEach(subField => {
                            allFieldsMapped.add(`technicalDetails.${subField}`);
                        });
                    } else {
                        allFieldsMapped.add(field);
                    }
                });
            }
        });

        return {
            extractedData,
            mappedFields: Array.from(allFieldsMapped),
            totalFields: allFieldsMapped.size
        };
    }, [ocrResults]);

    /**
     * Check if a field was filled by OCR (for highlighting)
     */
    const isOCRField = useCallback((fieldName) => {
        return ocrFields.has(fieldName);
    }, [ocrFields]);

    /**
     * Get highlight color based on document type
     */
    const getFieldHighlightClass = useCallback((fieldName) => {
        if (!ocrFields.has(fieldName)) return '';

        const documentType = ocrFields.get(fieldName);

        switch (documentType) {
            case 'alis_sozlesmesi':
                return 'bg-warning bg-opacity-25 border-warning'; // 🟡 Sarı
            case 'satis_sozlesmesi':
                return 'bg-info bg-opacity-25 border-info'; // 🔵 Mavi
            case 'ruhsat':
                return 'bg-success bg-opacity-25 border-success'; // 🟢 Yeşil
            default:
                return 'bg-secondary bg-opacity-25 border-secondary'; // ⚫ Gri
        }
    }, [ocrFields]);

    /**
     * Get OCR confidence for a specific document type
     */
    const getOCRConfidence = useCallback((documentType) => {
        return ocrResults[documentType]?.confidence || 0;
    }, [ocrResults]);

    /**
     * Clear all OCR data
     */
    const clearOCRData = useCallback(() => {
        console.log('🧹 Clearing all OCR data');
        setOcrResults({ ruhsat: null, alis: null, satis: null });
        setOcrFields(new Set());
        setErrors({});
        setBatchProgress({ current: 0, total: 0 });
        processingRef.current = false;
    }, []);

    /**
     * Cancel ongoing OCR processing
     */
    const cancelProcessing = useCallback(() => {
        console.log('⏹️ Cancelling OCR processing');
        processingRef.current = false;
        setProcessing(false);
    }, []);

    /**
     * Get OCR summary for UI display
     */
    const getOCRSummary = useCallback(() => {
        const processedDocs = Object.values(ocrResults).filter(Boolean);
        const totalFields = ocrFields.size;
        const avgConfidence = processedDocs.length > 0
            ? Math.round(processedDocs.reduce((sum, doc) => sum + doc.confidence, 0) / processedDocs.length)
            : 0;

        return {
            documentsProcessed: processedDocs.length,
            fieldsExtracted: totalFields,
            averageConfidence: avgConfidence,
            hasErrors: Object.keys(errors).length > 0
        };
    }, [ocrResults, ocrFields, errors]);

    // ✅ RETURN HOOK API
    return {
        // State
        processing,
        ocrResults,
        ocrFields,
        errors,
        batchProgress,

        // Actions
        processDocument,
        processBatchDocuments,
        clearOCRData,
        cancelProcessing,

        // Utilities
        isOCRField,
        getFieldHighlightClass,
        getOCRConfidence,
        getOCRSummary,
        getExtractedData,

        // Computed
        hasOCRData: Object.values(ocrResults).some(Boolean),
        totalOCRFields: ocrFields.size
    };
};

export default useOCR;